import { Link } from 'react-router-dom'
import { useSiteContent } from '@/hooks/useSiteContent'
import {
  getEmailError,
  getIndianMobileError,
  getWebsiteUrlError,
  isValidEmail,
  isValidIndianMobile,
  isValidWebsiteUrl,
  phonePattern,
  submitInquiry,
} from '@/lib/inquiries'
import { FormEvent, useState } from 'react'

const BrandsPage = () => {
  const { content } = useSiteContent()
  const [phoneError, setPhoneError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [urlError, setUrlError] = useState('')
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [formMessage, setFormMessage] = useState('')

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const form = event.currentTarget
    const formData = new FormData(form)
    const contactNumber = String(formData.get('contactNumber') || '')
    const brandUrl = String(formData.get('brandUrl') || '')
    const email = String(formData.get('email') || '')
    const currentPhoneError = getIndianMobileError(contactNumber)
    const currentUrlError = getWebsiteUrlError(brandUrl)
    const currentEmailError = getEmailError(email)

    if (
      currentPhoneError ||
      currentUrlError ||
      currentEmailError ||
      !isValidIndianMobile(contactNumber) ||
      !isValidWebsiteUrl(brandUrl) ||
      !isValidEmail(email)
    ) {
      setPhoneError(currentPhoneError || (!isValidIndianMobile(contactNumber) ? 'Enter a valid 10 digit Indian mobile number.' : ''))
      setUrlError(currentUrlError)
      setEmailError(currentEmailError || (!isValidEmail(email) ? 'Enter a valid email address.' : ''))
      setFormStatus('idle')
      return
    }

    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }

    setPhoneError('')
    setUrlError('')
    setEmailError('')
    setFormStatus('submitting')
    setFormMessage('')

    try {
      await submitInquiry('brand', {
        name: String(formData.get('name') || ''),
        brandName: String(formData.get('brandName') || ''),
        brandUrl,
        contactNumber,
        email,
        message: String(formData.get('message') || ''),
      })

      form.reset()
      setFormStatus('success')
      setFormMessage('Thanks. Your brand enquiry has been sent to Shelf Discovery.')
    } catch {
      setFormStatus('error')
      setFormMessage('We could not send this right now. Please email support@shelfdiscovery.in.')
    }
  }

  return (
    <main className="site-shell">
      <header className="site-nav">
        <Link to="/" className="brand-mark">{content.nav.brand}</Link>
        <nav aria-label="Primary navigation">
          <Link to="/real-estate" className="nav-cta">{content.nav.shelfOwnersCta}</Link>
          <Link to="/brands" className="nav-cta">{content.nav.brandsCta}</Link>
        </nav>
      </header>

      <section className="info-page">
        <div className="info-page__content">
          <p className="info-page__eyebrow">{content.brandsPage.eyebrow}</p>
          <h1>{content.brandsPage.title}</h1>
          <p>{content.brandsPage.body}</p>
        </div>

        <form
          className="contact-form"
          onSubmit={handleSubmit}
          noValidate
        >
          <div>
            <p className="info-page__eyebrow">{content.brandsPage.formEyebrow}</p>
            <h2>{content.brandsPage.formTitle}</h2>
          </div>
          <label>
            Name
            <input name="name" type="text" autoComplete="name" required />
          </label>
          <label>
            Brand name
            <input name="brandName" type="text" required />
          </label>
          <label>
            Brand URL
            <input
              name="brandUrl"
              type="url"
              inputMode="url"
              autoComplete="url"
              placeholder="https://"
              aria-invalid={Boolean(urlError)}
              aria-describedby={urlError ? 'brand-url-error' : undefined}
              onChange={(event) => setUrlError(getWebsiteUrlError(event.currentTarget.value))}
            />
            {urlError ? <span className="field-error" id="brand-url-error">{urlError}</span> : null}
          </label>
          <label>
            Contact number
            <input
              name="contactNumber"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              pattern={phonePattern}
              placeholder="+91 98765 43210"
              title="Enter a valid 10 digit Indian mobile number."
              aria-invalid={Boolean(phoneError)}
              aria-describedby={phoneError ? 'brand-phone-error' : undefined}
              onChange={(event) => setPhoneError(getIndianMobileError(event.currentTarget.value))}
              required
            />
            {phoneError ? <span className="field-error" id="brand-phone-error">{phoneError}</span> : null}
          </label>
          <label>
            Email
            <input
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-invalid={Boolean(emailError)}
              aria-describedby={emailError ? 'brand-email-error' : undefined}
              onChange={(event) => setEmailError(getEmailError(event.currentTarget.value))}
              required
            />
            {emailError ? <span className="field-error" id="brand-email-error">{emailError}</span> : null}
          </label>
          <label>
            Message
            <textarea name="message" rows={5} required />
          </label>
          <button type="submit" disabled={formStatus === 'submitting'}>
            {formStatus === 'submitting' ? 'Sending...' : 'Send enquiry'}
          </button>
          {formMessage ? <p className={`form-message form-message--${formStatus}`}>{formMessage}</p> : null}
        </form>
      </section>
    </main>
  )
}

export default BrandsPage
